import React from 'react';
import { Body5, Checkbox, Container } from '@ui';
import { FlatButton } from '@ui/buttons';
import { FormState } from '@ui/FormState';
import { Working } from '@ui/Working';
import { handle400Statuses, sendApiRequest } from '@/services/api';
import { logNetRequest } from '@/utils';
import { AdvancedSettingsButton } from '@/containers/DigitalAnalyst/AdvancedSettings';
import { addAdvancedSettingValues, useAdvancedSettings } from '@/hooks/application/useAdvancedSettings';
import { useExtraModeParams } from '@/hooks/features/useExtraModeParams';
import { ModeContext } from '@/contexts/ModeContext';
import { FormStateContext, FormStateProvider } from '@/contexts/FormStateContext';
import { ExtraModeParams } from '@/containers/DigitalAnalyst/components/ExtraModeParams';
import { Sources } from '@features/query/form/Sources';
import { Field } from '@features/query/form/Field';
import { fetchAvailableRfpDocs, isRunnableTemplate } from '@services/rfp';
import { SubmitButtonWrapper, FormWrapper } from './styles';
import { DocumentUploadStatusFlatDDQTemplateGeneration } from '../document/upload/DocumentUploadStatus';
import { normalizeTemplateQueryMetadata } from './rfpTemplateAnswerUtils';
import { submitTemplate, submitTemplateStream } from '@services/genaiQueriesV2';

const defaultFormState = {
  selectedDocs: [],
  tags: [],
};

function convertModeParamsToApiFormat(modeParams) {
  return Object.entries(modeParams).reduce((output, [key, value]) => {
    if (key.includes('_section_header')) {
      return output;
    }

    output[key] = value;
    return output;
  }, {});
}

const DefaultDocsSync = () => {
  const { autoSelectDefaultDocuments, tool } = React.useContext(ModeContext);
  const { setFormStateValue } = React.useContext(FormStateContext);

  React.useEffect(() => {
    const canceled = { isCanceled: false };
    const fetchDefaultDocs = async () => {
      const URL = 'v1/genai/documents/default?user_mode=' + tool;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(URL, status, data);
      handle400Statuses(status);
      if (status === 200 && !canceled.isCanceled) {
        if (data.documents.length > 0) {
          setFormStateValue('selectedDocs', data.documents);
        }
      }
    };

    if (autoSelectDefaultDocuments) {
      fetchDefaultDocs();
    }
    return () => {
      canceled.isCanceled = true;
    };
  }, []);

  return null;
};

const GenerateButton = ({
  selectedDocId,
  onGenerate,
  modeExtraParams,
  modeExtraParamsState,
  isStreamingEnabled = true,
}) => {
  const { tool, modeType } = React.useContext(ModeContext);
  const { state: formState } = React.useContext(FormStateContext);
  const advancedSettingsValues = useAdvancedSettings();
  const [formError, setFormError] = React.useState(null);
  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);

  const handleGenerate = async () => {
    if (!selectedDocId) {
      setFormError('Please select a document');
      return;
    }

    setFormError(null);
    setIsSubmittingToApi(true);

    const docIds = formState.selectedDocs.map((d) => d.id);
    const modeParams = modeExtraParams.length > 0 ? convertModeParamsToApiFormat({ ...modeExtraParamsState }) : undefined;

    const settings = { user_mode: tool };
    addAdvancedSettingValues(advancedSettingsValues, settings);
    const submitFn = isStreamingEnabled ? submitTemplateStream : submitTemplate;
    const result = await submitFn({
      modeType,
      templateId: selectedDocId,
      docIds,
      modeParams,
      settings,
    });
    if (result.status === 200) {
      const normalizedQueryListMetadata = normalizeTemplateQueryMetadata(result.data?.template_queries);
      onGenerate({
        resultId: result.runId,
        streamingId: isStreamingEnabled ? result.runId : null,
        agentRecordId: null,
        templateRunId: result.runId,
        queryListMetadata: normalizedQueryListMetadata,
        sourceDocIds: docIds,
      });
    } else {
      setFormError('Something went wrong, try again');
    }
    setIsSubmittingToApi(false);
  };

  if (isSubmittingToApi) {
    return (
      <Container top={24}>
        <Working message="Generating Report" />
      </Container>
    );
  }

  return (
    <>
      <Container top={16} centerAll>
        <FormState error={formError} />
      </Container>
      <Container top={24}>
        <SubmitButtonWrapper>
          <FlatButton
            disabled={!selectedDocId}
            onClick={handleGenerate}
            fullWidth
          >
            Generate Report
          </FlatButton>
        </SubmitButtonWrapper>
      </Container>
    </>
  );
};

export function ReadyPhase({
  onGenerate,
  onTemplateSelect,
  onGoToUpload,
  isStreamingEnabled = true,
  onStreamingEnabledChange = () => {},
}) {
  const { tool, modeType } = React.useContext(ModeContext);
  const { modeExtraParams, state: modeExtraParamsState, onChangeModeExtraParams } = useExtraModeParams(modeType);

  const [docs, setDocs] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isRefreshingDocs, setIsRefreshingDocs] = React.useState(false);
  const [selectedDocId, setSelectedDocId] = React.useState('');
  const [loadError, setLoadError] = React.useState(null);
  const isFetchingDocsRef = React.useRef(false);

  const refreshDocs = React.useCallback(async ({ isRefresh = false } = {}) => {
    if (isFetchingDocsRef.current) {
      return;
    }

    isFetchingDocsRef.current = true;
    setLoadError(null);

    if (isRefresh) {
      setIsRefreshingDocs(true);
    } else {
      setIsLoading(true);
    }

    try {
      const { ok, data, error } = await fetchAvailableRfpDocs();

      if (ok) {
        const list = Array.isArray(data) ? data : data.documents || [];
        setDocs(list.filter(isRunnableTemplate));
      } else {
        setLoadError(error || 'Something went wrong loading available documents. Please try again.');
      }
    } finally {
      if (isRefresh) {
        setIsRefreshingDocs(false);
      } else {
        setIsLoading(false);
      }

      isFetchingDocsRef.current = false;
    }
  }, []);

  React.useEffect(() => {
    refreshDocs();
  }, [refreshDocs]);

  React.useEffect(() => {
    if (!selectedDocId) {
      return;
    }

    const selectedDocStillExists = docs.some((doc) => String(doc.id) === String(selectedDocId));
    if (!selectedDocStillExists) {
      setSelectedDocId('');
      onTemplateSelect(null);
    }
  }, [docs, onTemplateSelect, selectedDocId]);

  const handleFileUploadComplete = React.useCallback(() => {
    refreshDocs({ isRefresh: true });
  }, [refreshDocs]);

  const handleSelectChange = (e) => {
    const nextSelectedDocId = e.target.value;
    const selectedTemplate = docs.find((doc) => String(doc.id) === String(nextSelectedDocId)) || null;
    setSelectedDocId(nextSelectedDocId);
    onTemplateSelect(selectedTemplate);
  };

  /** Checkbox calls `onChange(name)(!value)` — first call is ignored here, second receives the boolean. */
  const handleStreamingCheckboxChange = React.useCallback(
    () => (checked) => onStreamingEnabledChange(checked),
    [onStreamingEnabledChange]
  );

  if (isLoading) {
    return (
      <Container top={24}>
        <Working message="Loading available documents" />
      </Container>
    );
  }

  if (loadError && docs.length === 0) {
    return (
      <Container top={24}>
        <Body5>{loadError}</Body5>
      </Container>
    );
  }

  if (docs.length === 0) {
    return (
      <Container top={24}>
        <Body5>No uploaded templates available. Upload a Word document or PowerPoint deck first.</Body5>
        <Container top={16} centerAll>
          <FormState error={loadError} />
        </Container>
        <Container top={24}>
          <FlatButton onClick={onGoToUpload}>Create Template From Document or Deck</FlatButton>
        </Container>
        <Container top={24}>
          <DocumentUploadStatusFlatDDQTemplateGeneration onFileUploadComplete={handleFileUploadComplete} />
        </Container>
        <Container top={24} />
      </Container>
    );
  }

  return (
    <Container top={24}>
      <FormStateProvider initialStateParam={defaultFormState}>
        <DefaultDocsSync />
        <FormWrapper>
          <AdvancedSettingsButton tool={tool} />
          <Container bottom={16} centerAll>
            <FormState error={loadError} />
          </Container>
          <Container>
            <Body5 bold>Select Template</Body5>
          </Container>
          <Container top={16}>
            {isRefreshingDocs ? (
              <Working message="Refreshing available templates" />
            ) : (
              <select value={selectedDocId} onChange={handleSelectChange}>
                <option value="">Select a document...</option>
                {docs.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name || doc.filename}
                  </option>
                ))}
              </select>
            )}
          </Container>
          {modeExtraParams.length > 0 && (
            <>
              <Container top={24}>
                <Body5 bold>Parameters</Body5>
              </Container>
              <ExtraModeParams
                top={16}
                modeExtraParams={modeExtraParams}
                modeExtraParamsState={modeExtraParamsState}
                onChangeModeExtraParams={onChangeModeExtraParams}
              />
            </>
          )}
          <Container top={16}>
            <Field label="Source Documents" component={<Sources />} boldLabel />
          </Container>

          <Container top={24} row verticallyCenter>
            <Checkbox
              name="documentPreviewStreaming"
              value={isStreamingEnabled}
              onChange={handleStreamingCheckboxChange}
            />
            <div
              onClick={() => onStreamingEnabledChange(!isStreamingEnabled)}
              style={{ cursor: 'pointer' }}
              role="presentation"
            >
              <Container bottom={3}>
                <Body5>Enable Document Preview</Body5>
              </Container>
            </div>
          </Container>

          <GenerateButton
            isStreamingEnabled={isStreamingEnabled}
            selectedDocId={selectedDocId}
            onGenerate={onGenerate}
            modeExtraParams={modeExtraParams}
            modeExtraParamsState={modeExtraParamsState}
          />
        </FormWrapper>
      </FormStateProvider>
      <Container top={24}>
        <DocumentUploadStatusFlatDDQTemplateGeneration hideIfNoDocs onFileUploadComplete={handleFileUploadComplete} />
      </Container>
    </Container>
  );
}
